import { withRouter } from 'react-router-dom';
import { isBefore, isToday, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import classnames from 'classnames';

import CellSelect from './cell_select';
import CellDatetimePick from './cell_datetime_pick';
import CellDatetimeSinglePick from './cell_datetime_single_pick';
import CellDateSinglePick from './cell_date_single_pick';
import CellTextEdit from './cell_text_edit';

import { ReactComponent as PencilSVG } from 'icons/pencil.svg';
import { ReactComponent as ExpandSVG } from 'icons/expand.svg';
import { ReactComponent as CheckSVG } from 'icons/table/check.svg';
import { ReactComponent as TrashSVG } from 'icons/table/trash.svg';
import { ReactComponent as CheckCircleSVG } from 'icons/table/check-circle.svg';
import { ReactComponent as ClockSVG } from 'icons/table/clock.svg';
import { ReactComponent as ExclamationSVG } from 'icons/table/exclamation.svg';
import { ReactComponent as LinkSVG } from 'icons/link.svg';

import { formatDatetime, formatDate, parseDatetime } from 'helpers/timezone';
import { getHeight, getWeight } from 'helpers/health';
import { columnShape, rowShape } from 'helpers/shared_types';
import { getHintFromPaymentStatus } from 'helpers/hint';
import { nameDecorator } from 'helpers/name';
import { phoneDecorator } from 'helpers/phone';

import { INVOICE_PAYMENT_STATUSES } from 'config/invoice';

const Cell = ({
  column,
  data,
  history,
  onSelect,
  onDatePick,
  onSingleDatePick,
  onTextUpdate,
  onStatusUpdate,
  onDelete,
  onLink,
}) => {
  let owner = '';
  let submitDateAndTime = formatDatetime(data.createdAt);
  const classNames = classnames(data.classNames, column.classNames);
  const doctor = data.doctor
    ? `${data.doctor.lastName?.toUpperCase()} ${data.doctor.firstName}`
    : 'Blank';
  const nurse = data.nurse
    ? `${data.nurse.firstName} ${data.nurse.lastName}`
    : 'Blank';
  const value = data.technician
    ? `${data.technician.firstName} ${data.technician.lastName}`
    : '';
  const [p, a] = column.accessor.split('.');
  const [acc1, acc2] = column.accessor.split('.');

  const submitDateAndTimeField = data.fields?.find(
    (f) => f?.label === 'Submit date and time',
  );

  // if data is a response
  if (data.variables && submitDateAndTimeField) {
    submitDateAndTime = submitDateAndTimeField.value;
  }

  const style =
    data.status === 'incomplete'
      ? {
          stroke: '#c5d2d9',
          fill: '#c5d2d9',
        }
      : {
          stroke: '#22c7a9',
          fill: '#22c7a9',
        };
  let date;
  if (data.dueDate instanceof Date) {
    date = data.dueDate;
  } else {
    date = parseISO(data.dueDate);
  }
  const isHighlighted =
    data.status === 'incomplete' &&
    (isBefore(date, new Date()) || isToday(date));

  const onCustomerColumnClick = () => {
    window.open(`/customers/${data.id}`, '_blank');
  };

  switch (column.type) {
    case 'text':
      return (
        <div
          data-cy={column.accessor}
          className={`${classNames} th-list-data middarkgrey f8`}
        >
          {data[column.accessor]}
        </div>
      );
    case 'customerText':
      if (!data.customer) return <div className="th-list-data" />;
      return (
        <div data-cy={column.accessor} className="th-list-data middarkgrey f8">
          {data.customer[column.accessor]}
        </div>
      );
    case 'healthData':
      if (!data.customer) return <div className="th-list-data" />;
      return (
        <div data-cy={column.accessor} className="th-list-data pointer">
          <h3 className="ma0 pa0 th-members-list-name ">Height</h3>
          <p className="apps-card-app-desc">{getHeight(data.customer)}</p>
          <h3 className="th-members-list-name ">Weight</h3>
          <p className="apps-card-app-desc">{getWeight(data.customer)}</p>
        </div>
      );
    case 'textWithEdit':
      return (
        <CellTextEdit
          label={column.accessor}
          value={data.customer && data.customer[column.accessor]}
          onTextUpdate={onTextUpdate}
          readOnly={column.readOnly}
        />
      );
    case 'textWithEditDirect':
      return (
        <CellTextEdit
          label={column.accessor}
          value={data[column.accessor]}
          onTextUpdate={onTextUpdate}
          readOnly={column.readOnly}
        />
      );
    case 'actions':
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <div className="flex items-center justify-around">
            <div
              className="pointer"
              onClick={() =>
                window.open(`/${column.accessor}/${data.id}`, '_blank')
              }
            >
              <ExpandSVG />
            </div>
            <div
              className="pointer"
              onClick={() =>
                window.open(`/${column.accessor}/${data.id}/edit`, '_blank')
              }
            >
              <PencilSVG />
            </div>
          </div>
        </div>
      );
    case 'itemAction':
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <div className="flex items-center justify-around">
            <div
              className="pointer"
              onClick={() =>
                window.open(`/${column.accessor}/${data.id}`, '_blank')
              }
            >
              <ExpandSVG />
            </div>
          </div>
        </div>
      );
    case 'deleteAction':
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <div className="flex items-center justify-around">
            <div className="pointer" onClick={() => onDelete()}>
              <TrashSVG />
            </div>
          </div>
        </div>
      );
    case 'linkAction': {
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <div className="flex items-center justify-around">
            <div className="pointer" onClick={() => onLink()}>
              <LinkSVG />
            </div>
          </div>
        </div>
      );
    }
    case 'customer': {
      const { customer } = data;

      if (!customer)
        return <div data-cy={column.accessor} className="th-list-data" />;

      const wrapperClasses = classnames('th-list-data', column.classNames);

      return (
        <div data-cy={column.accessor} className={wrapperClasses}>
          {customer.firstName || customer.lastName ? (
            <h3
              className="ma0 pa0 underline pointer"
              onClick={() => window.open(`/customers/${customer.id}`, '_blank')}
            >
              {nameDecorator(customer.firstName, customer.lastName)}
            </h3>
          ) : (
            <h3
              className="ma0 pa0 underline pointer"
              onClick={() => window.open(`/customers/${customer.id}`, '_blank')}
            >
              {customer.email}
            </h3>
          )}
          {customer.phone && (
            <a
              href={`tel:${phoneDecorator(customer.phone)}`}
              className="ma0 pa0 middarkgrey f8 th-members-list-email underline"
            >
              {phoneDecorator(customer.phone)}
            </a>
          )}
        </div>
      );
    }
    case 'customerFirstNameLastName': {
      if (!data.customer)
        return <div data-cy={column.accessor} className="th-list-data" />;

      const wrapperClasses = classnames('th-list-data', column.classNames);

      return (
        <div
          data-cy={column.accessor}
          className={wrapperClasses}
          onClick={() =>
            window.open(`/customers/${data.customer.id}`, '_blank')
          }
        >
          <h3 className="ma0 pa0 th-members-list-name">
            {nameDecorator(data.customer.firstName, data.customer.lastName)}
          </h3>
        </div>
      );
    }
    case 'customerName':
      return (
        <div data-cy={column.accessor} className="th-list-data th-list-sticky">
          {data.firstName || data.lastName ? (
            <h3
              className="ma0 pa0 th-members-list-name underline pointer"
              onClick={() => window.open(`/customers/${data.id}`, '_blank')}
            >
              {nameDecorator(data.firstName, data.lastName)}
            </h3>
          ) : (
            <h3
              className="ma0 pa0 th-members-list-name underline pointer"
              onClick={() => window.open(`/customers/${data.id}`, '_blank')}
            >
              {data.email}
            </h3>
          )}
        </div>
      );
    case 'customerContact':
      return (
        <div
          data-cy={column.accessor}
          className="th-list-data th-list-sticky"
          onClick={() => window.open(`/customers/${data.id}`, '_blank')}
        >
          {data.phone && (
            <a
              href={`tel:${phoneDecorator(data.phone)}`}
              className="ma0 pa0 middarkgrey f8 th-members-list-email underline"
            >
              {phoneDecorator(data.phone)}
            </a>
          )}
        </div>
      );
    case 'dateOfBirth':
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <p className="apps-card-app-desc">{formatDate(data.dateOfBirth)}</p>
        </div>
      );
    case 'invoiceDate':
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <p className="apps-card-app-desc">{formatDate(data.createdAt)}</p>
        </div>
      );
    case 'dueDate':
      return (
        <CellDateSinglePick
          label="Due date"
          date={parseDatetime(data.dueDate)}
          onDateUpdate={({ date }) => onSingleDatePick(date)}
        />
      );
    case 'customerDirect':
      return (
        <div data-cy={column.accessor} className="th-list-data th-list-sticky">
          {data.firstName || data.lastName ? (
            <h3
              className="ma0 pa0 th-members-list-name underline pointer"
              onClick={onCustomerColumnClick}
            >
              {nameDecorator(data.firstName, data.lastName)}
            </h3>
          ) : (
            <h3
              className="ma0 pa0 th-members-list-name underline pointer"
              onClick={() => window.open(`/customers/${data.id}`, '_blank')}
            >
              {data.email}
            </h3>
          )}
          {data.phone && (
            <a
              href={`tel:${phoneDecorator(data.phone)}`}
              className="ma0 pa0 middarkgrey f8 th-members-list-email underline"
            >
              {phoneDecorator(data.phone)}
            </a>
          )}
        </div>
      );
    case 'doctorFullName':
      return (
        <div
          data-cy={column.accessor}
          className="th-list-data pointer th-list-sticky th-list-cellwidth-240 pointer"
          onClick={() => history.push(`doctors/${data.id}`)}
        >
          <h3 className="ma0 pa0 th-members-list-name">
            {data.lastName?.toUpperCase()} {data.firstName}
          </h3>
        </div>
      );
    case 'accountType':
      return (
        <div
          data-cy={column.accessor}
          className="th-list-cellwidth-240 th-list-data middarkgrey f8"
        >
          {data.type === 'sleep'
            ? 'Sleep doctor account'
            : 'Non-sleep doctor account'}
        </div>
      );
    case 'specialty':
      return (
        <div
          data-cy={column.accessor}
          className="th-list-cellwidth-240 th-list-data middarkgrey f8"
        >
          {data.specialty &&
            column.options.find((o) => o.id === data.specialty).label}
        </div>
      );
    case 'accountStatus':
      return (
        <div
          data-cy={column.accessor}
          className="th-list-cellwidth-240 th-list-data middarkgrey f8"
        >
          {data.isActive ? 'Active' : 'Inactive'}
        </div>
      );
    case 'equipmentStatus':
      return (
        <CellSelect
          value={data.equipmentStatus}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options}
          readOnly={column.readOnly}
        />
      );
    case 'doctor':
      return (
        <CellSelect
          value={doctor}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options.filter((o) => o.isActive || o.id === 'blank')}
          readOnly={column.readOnly}
        />
      );
    case 'nurse':
      return (
        <CellSelect
          value={nurse}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options}
          readOnly={column.readOnly}
        />
      );
    case 'treatingOrg':
      return (
        <CellSelect
          value={data.treatingOrg?.name}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options}
          readOnly={column.readOnly}
        />
      );
    case 'owner':
      if (data.owner) {
        if (data.owner.role === 'admin') {
          owner = `${data.owner.admin.firstName} ${data.owner.admin.lastName}`;
        } else if (data.owner.role === 'nurse') {
          owner = `${data.owner.nurse.firstName} ${data.owner.nurse.lastName}`;
        }
      }

      return (
        <CellSelect
          value={owner}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options.filter((o) => o.id !== 'blank')}
          readOnly={column.readOnly}
        />
      );
    case 'doctorText':
    case 'technician':
      return (
        <CellSelect
          value={value}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options.filter((o) => o.isActive || o.id === 'blank')}
          readOnly={column.readOnly}
        />
      );
    case 'datetimePicker':
      return (
        <CellDatetimePick
          pickupDate={parseDatetime(data.pickupDate)}
          deliveryDate={parseDatetime(data.deliveryDate)}
          onDateUpdate={({ pickupDate, deliveryDate }) =>
            onDatePick(pickupDate, deliveryDate)
          }
          readOnly={column.readOnly}
        />
      );
    case 'singleDatetimePicker':
      return (
        <CellDatetimeSinglePick
          date={parseDatetime(data[p][a])}
          onDateUpdate={({ date }) => onSingleDatePick(date)}
        />
      );
    case 'singleDatePicker':
      return (
        <CellDateSinglePick
          label="Referral date"
          readOnly={column.readOnly}
          date={parseDatetime(data[column.accessor])}
          onDateUpdate={({ date }) => onSingleDatePick(date)}
        />
      );
    case 'event':
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <h3 className="ma0 pa0 th-members-list-name ">
            {data.cancelled && (
              <span role="img" aria-label="x" className="f-small">
                &#10060;&nbsp;
              </span>
            )}
            {data.name}
          </h3>
          <p className="apps-card-app-desc">
            {formatDatetime(data.startTime)} - {formatDatetime(data.endTime)}
          </p>
        </div>
      );
    case 'calendarEvent':
      return (
        <div data-cy={column.accessor} className="th-list-data">
          <h3 className="ma0 pa0 th-members-list-name ">{data.title}</h3>
          <p className="apps-card-app-desc">
            {formatDatetime(data.start)} - {formatDatetime(data.end)}
          </p>
          {data.cancelled && (
            <span className="th-badge th-badge-red mt5">Cancelled</span>
          )}
        </div>
      );
    case 'select':
      return (
        <CellSelect
          data-cy={column.accessor}
          value={data[column.accessor]}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options}
          readOnly={column.readOnly}
        />
      );
    case 'customerSelect':
      return (
        <CellSelect
          value={data.customer[column.accessor]}
          onUpdate={(value) => onSelect(column.accessor, value)}
          options={column.options}
          readOnly={column.readOnly}
        />
      );
    case 'customerDataSelect':
      return (
        <CellSelect
          value={data[acc1][acc2]}
          onUpdate={(value) => onSelect(data[acc1][acc2], value)}
          options={column.options}
          readOnly={column.readOnly}
        />
      );
    case 'customerDetail':
      if (!data.customer) return <div className="th-list-data" />;
      return (
        <div className="th-list-data">
          <p className="apps-card-app-desc">{data.customer[column.accessor]}</p>
        </div>
      );
    case 'customerDateOfBirth':
      return (
        <div className="th-list-data">
          <p className="apps-card-app-desc">
            {formatDate(data.customer.dateOfBirth)}
          </p>
        </div>
      );
    case 'file':
      return (
        <div className="th-list-data">
          <p className="apps-card-app-desc">
            {data.customer.files &&
            data.customer.files.find((f) => f.type === column.accessor)
              ? 'Uploaded'
              : 'Not uploaded'}
          </p>
        </div>
      );
    case 'submitDateAndTime':
      return (
        <div className="th-list-data middarkgrey f8">{submitDateAndTime}</div>
      );
    case 'payment': {
      const wrapperClasses = classnames('th-badge mt5', {
        'th-badge-green': data.currentPaymentStatus === 'succeeded',
        'th-badge-grey': data.currentPaymentStatus === 'incomplete',
        'th-badge-pink': data.currentPaymentStatus === 'failed',
      });

      const icon =
        data.currentPaymentStatus === 'succeeded' ? (
          <CheckCircleSVG />
        ) : data.currentPaymentStatus === 'incomplete' ? (
          <ClockSVG />
        ) : (
          <ExclamationSVG />
        );
      return (
        <div data-cy={column.accessor} className="th-list-data">
          {data.currentPaymentStatus && (
            <span
              className={wrapperClasses}
              data-rh={getHintFromPaymentStatus[data.currentPaymentStatus]}
            >
              {icon}€{data.currentPaymentAmount}
            </span>
          )}
        </div>
      );
    }
    case 'invoicePaymentStatus': {
      const wrapperClasses = classnames('th-badge', {
        'th-badge-gray': ['in-progress', 'abandoned'].includes(
          data.invoicePaymentStatus,
        ),
        'th-badge-yellow': data.invoicePaymentStatus === 'in-anomaly',
        'th-badge-pink': ['rejected'].includes(data.invoicePaymentStatus),
        'th-badge-green': ['paid', 'cashed'].includes(
          data.invoicePaymentStatus,
        ),
      });

      const humanizedStatus = INVOICE_PAYMENT_STATUSES.find(
        (s) => s.id === data.invoicePaymentStatus,
      ).label;

      return (
        <div data-cy={column.accessor} className="th-list-data">
          <span className={wrapperClasses}>{humanizedStatus}</span>
        </div>
      );
    }
    case 'amount': {
      if (!data.amounts)
        return (
          <div
            data-cy={column.accessor}
            className={`${classNames} th-list-data middarkgrey f8`}
          ></div>
        );

      const amountValue =
        data.amounts[column.accessor] === '0' ||
        data.amounts[column.accessor] === '0.0'
          ? ''
          : `€${data.amounts[column.accessor]}`;

      return (
        <div
          data-cy={column.accessor}
          className={`${classNames} th-list-data middarkgrey f8`}
        >
          {amountValue}
        </div>
      );
    }
    case 'createdAt':
      return (
        <div className="th-list-data middarkgrey f8">
          {formatDatetime(data[column.accessor])}
        </div>
      );
    case 'statusCheckbox': {
      const wrapperClasses = classnames(
        'th-list-data-no-padding th-list-sticky pointer',
        {
          'th-highlighted': isHighlighted,
        },
      );

      return (
        <div className={wrapperClasses} onClick={() => onStatusUpdate()}>
          <div className="th-status">
            <CheckSVG style={style} />
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};

Cell.propTypes = {
  column: columnShape,
  data: rowShape,
  history: ReactRouterPropTypes.history.isRequired,
  onSelect: PropTypes.func,
  onDatePick: PropTypes.func,
  onSingleDatePick: PropTypes.func,
  onTextUpdate: PropTypes.func,
  onStatusUpdate: PropTypes.func,
  onDelete: PropTypes.func,
};

export default withRouter(Cell);
